/**
 * Is this track ready to mix: are its cue points in place?
 *
 * One compact cell per grid row, so a curator scanning a thousand tracks sees
 * which ones need attention without opening any of them.
 *
 * **Loud only where silence is the consequence.** A missing Cue In or Early Out
 * can put silence into a mix, so it flashes. A missing Intro, Hook or ending
 * type only switches a feature off for that record, so it is a quiet count with
 * the details in the tooltip -- a grid where every row blinks is a grid where
 * nothing does.
 */

import type { Track } from "../types";

const LABEL: Record<string, string> = {
  cueIn: "Start",
  cueOut: "Mix out",
  intro: "Intro",
  hook: "Highlight",
  ending: "Ending",
};

export function ReadyCell({ track }: { track: Track }) {
  const cues = track.cues;
  if (!cues) return <span className="skeleton w-sm" />;
  const critical = cues.critical;
  const advisory = cues.missing.filter((m) => !critical.includes(m));

  return (
    <span className="ready-cell">
      {critical.map((m) => (
        <span key={m} className="badge critical flash" title={`No ${LABEL[m]} set — songs may start or end on silence`}>
          {m === "cueIn" ? "No start" : "No mix out"}
        </span>
      ))}
      {critical.length === 0 && advisory.length === 0 && (
        <span className="badge ok" title="Start, intro, highlight, mix out and ending are all set">
          ✓
        </span>
      )}
      {advisory.length > 0 && (
        <span
          className="badge advisory"
          title={`Not set: ${advisory.map((m) => LABEL[m] ?? m).join(", ")}`}
        >
          {advisory.map((m) => (LABEL[m] ?? m)[0]).join(" ")}
        </span>
      )}
    </span>
  );
}
